import React, { useState } from "react"
import { useNavigate } from "react-router-dom"
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"
import {Crown, Users, Briefcase, Shield, MessageSquare, Mail, FileText, Settings, LogOut, Menu, X, ShieldCheck} from 'lucide-react'
import DashboardSwitcher from "./DashboardSwitcher"
import SuperadminProfileSwitcher from "./SuperadminProfileSwitcher"

interface SuperadminLayoutProps {
  children: React.ReactNode
  activeMenuItem?: string
}

export default function SuperadminLayout({ children, activeMenuItem = "" }: SuperadminLayoutProps) {
  const navigate = useNavigate()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)

  const handleSignOut = () => {
    lumi.auth.signOut()
    toast.success("Signed out successfully")
    navigate("/")
  }

  const menuItems = [
    { id: "overview", label: "Overview", icon: Crown, path: "/dashboard/superadmin" },
    { id: "users", label: "Users", icon: Users, path: "/dashboard/superadmin/users" },
    { id: "admins", label: "Admins", icon: ShieldCheck, path: "/dashboard/superadmin/admins" },
    { id: "applications", label: "Access Requests", icon: Briefcase, path: "/dashboard/superadmin?tab=applications" },
    { id: "invites", label: "Invites", icon: Shield, path: "/dashboard/superadmin/invites" },
    { id: "messages", label: "Messaging", icon: MessageSquare, path: "/dashboard/superadmin/messages" },
    { id: "email_analytics", label: "Email Analytics", icon: Mail, path: "/dashboard/superadmin/email-analytics" },
    { id: "audit", label: "Audit Logs", icon: FileText, path: "/dashboard/superadmin?tab=audit" },
    { id: "settings", label: "Settings", icon: Settings, path: "/dashboard/superadmin?tab=settings" }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-600 to-orange-600 text-white border-b border-amber-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Crown className="w-8 h-8" />
            <div>
              <h1 className="text-xl font-bold">Superadmin Dashboard</h1>
              <p className="text-sm text-amber-100">Full system access</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <SuperadminProfileSwitcher />
            <DashboardSwitcher />
            <button
              onClick={handleSignOut}
              className="hidden md:flex items-center gap-2 text-white hover:text-amber-200 transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Sign Out</span>
            </button>
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="md:hidden text-white hover:text-amber-200 transition-colors"
            >
              {isSidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className={`lg:col-span-1 ${
            isSidebarOpen ? "fixed inset-0 z-50 bg-black bg-opacity-50 md:relative md:bg-transparent" : "hidden lg:block"
          }`}>
            <nav className={`bg-white rounded-lg shadow-sm p-4 space-y-2 ${
              isSidebarOpen ? "fixed left-0 top-0 bottom-0 w-64 z-50 overflow-y-auto" : "sticky top-24"
            }`}>
              {isSidebarOpen && (
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 md:hidden">
                  <h2 className="text-lg font-bold text-gray-900">Menu</h2>
                  <button onClick={() => setIsSidebarOpen(false)} className="text-gray-600 hover:text-gray-900">
                    <X className="w-6 h-6" />
                  </button>
                </div>
              )}
              {menuItems.map(item => {
                const Icon = item.icon
                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      navigate(item.path)
                      setIsSidebarOpen(false)
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      activeMenuItem === item.id
                        ? "bg-amber-50 text-amber-700 font-semibold"
                        : "text-gray-600 hover:bg-gray-50"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{item.label}</span>
                  </button>
                )
              })}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            {children}
          </main>
        </div>
      </div>
    </div>
  )
}
